import _ from "lodash";
import React, { useEffect, useState } from "react";
import { ButtonGroup, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button } from "../../components";
import { DateInput, FormInput, FormSelect } from "../../components/forms";
import { CampaignProjectStatusSelect, CampaignSelect, GenericAdviserSelect, GenericUserDivisionSelect, MasterAccountSelect } from "../../components/forms/selects";
import { useFilterContext } from "../../hooks/FilterContext";
import ThemedModal from "../../components/utilities/ThemedModal";

const projectStatusOptions = [
    { label: "In Progress", value: 0 },
    { label: "Aborted", value: 1 },
    { label: "Completed", value: 2 },
    { label: "New Business", value: 3 },
    { label: "Valuation", value: 4 },
    { label: "Loop Closed", value: 5 },
    { label: "Expired", value: 6 },
    { label: "Planning", value: 7 },
    { label: "Not Started", value: 8 },
    { label: "Data Gathering", value: 9 },
    { label: "Ready to Appoint", value: 10 },
    { label: "Meeting Scheduled", value: 11 },
    { label: "Awaiting Paperwork", value: 12 },
    { label: "Unclassified", value: 13 }
];

const adviceTypeOptions = [
    { label: "Investment", value: 0 },
    { label: "Pension", value: 1 },
    { label: "Service Review", value: 2 },
    { label: "Estate Agency", value: 3 },
    { label: "Adviser Review", value: 4 },
    { label: "Protection", value: 5 },
    { label: "Mortgage", value: 6 },
    { label: "Execution Only", value: 7 },
    { label: "DB Scheme", value: 8 },
    { label: "Basic Review", value: 9 },
];

// Utility component to disable, and show a tooltip over, the Campaign Status selector when no campaign is selected
const CampaignDependentStatusSelect = ({ campaignId, isDisabled = false, ...rest }) => <OverlayTrigger
    placement="bottom"
    // trigger="hover"
    overlay={<Tooltip id="campaign-dependent-status-select-tooltip">
        Select a campaign to get status options for this project
    </Tooltip>}
>
    <span>
        <CampaignProjectStatusSelect {...rest} isDisabled={campaignId == null || isDisabled} />
    </span>
</OverlayTrigger>

export const ProjectManagementFiltersModal = ({
    handleClose, onFiltersChanged, show, ...rest
}) => {
    const { defaultFilters, filter, setFilter } = useFilterContext();
    const [tempFilters, setTempFilters] = useState(filter);

    const onApplyFilters = () => {
        setFilter((original) => ({
            ...original,
            ...tempFilters,
        }));
        onClose();
    };

    const onClearFilters = () => {
        setTempFilters(defaultFilters);
    };

    const onFilterValueChanged = (property, value) => {
        setTempFilters((original) => {
            return {
                ...original,
                [property]: value,
            };
        });
    };

    const onClose = () => {
        if (handleClose && typeof handleClose === "function") {
            handleClose();
        }
    };

    useEffect(() => {
        setTempFilters(oldTemp => !_.isEqual(oldTemp, filter)
            ? filter
            : oldTemp
        );
    }, [filter]);

    return <Modal size="xl" centered backdrop="static" show={show} onHide={handleClose}>
        <ThemedModal.Header closeButton>
            <Modal.Title>Project Management Filters</Modal.Title>
        </ThemedModal.Header>
        <Modal.Body {...rest}>
            <ProjectManagementFilters
                filter={tempFilters}
                setFilter={onFilterValueChanged} />
        </Modal.Body>
        <Modal.Footer>
            <Button variant="warning" onClick={onClearFilters} disabled={_.isEqual(tempFilters, defaultFilters)}>
                Reset Filters
            </Button>
            <Button variant="success" onClick={onApplyFilters}>
                Apply Filters
            </Button>
            <Button variant="light" onClick={onClose}>
                Cancel
            </Button>
        </Modal.Footer>
    </Modal>;
};

export const ProjectManagementFilters = ({ filter, setFilter }) => {

    const {
        divId,
        adviserIds,
        masterAccountIds,
        targetStartDate,
        targetEndDate,
        createdStartDate,
        createdEndDate,
        campaignId,
        campaignStatusIds,
        projectName,
        projectStatuses,
        adviceTypes
    } = filter;

    return <>
        <div className="row">
            <div className="col-12 col-lg-5 col-xl-4">
                <GenericUserDivisionSelect
                    label="Division"
                    defaultValue={divId}
                    onChange={selection => setFilter("divId", selection.value)}
                    disableAnimations
                />
            </div>
            <div className="col-12 col-lg-7 col-xl-4 mt-2 mt-lg-0">
                <GenericAdviserSelect
                    label="Adviser"
                    defaultValue={adviserIds}
                    onChange={(selections) => setFilter("adviserIds", selections.map(selection => selection.value))}
                    disableAnimations
                    isMulti
                />
            </div>
            <div className="col-12 col-lg-6 col-xl-4 mt-2 mt-xl-0">
                <MasterAccountSelect
                    label="Master Account"
                    value={masterAccountIds}
                    setValue={(selections) => setFilter("masterAccountIds", selections.map(selection => selection.value))}
                    isMulti
                    disableAnimations
                />
            </div>
            <div className="col-6 col-lg-3 col-xl mt-2">
                <DateInput
                    label="Target Start Date"
                    value={targetStartDate}
                    onBlur={value => setFilter("targetStartDate", value)}
                    disableAnimations
                    isClearable
                />
            </div>
            <div className="col-6 col-lg-3 col-xl mt-2">
                <DateInput
                    label="Target End Date"
                    value={targetEndDate}
                    onBlur={value => setFilter("targetEndDate", value)}
                    disableAnimations
                    isClearable
                />
            </div>
            <div className="col-6 col-lg-3 col-xl mt-2">
                <DateInput
                    label="Created Start Date"
                    value={createdStartDate}
                    onBlur={value => setFilter("createdStartDate", value)}
                    disableAnimations
                    isClearable
                />
            </div>
            <div className="col-6 col-lg-3 col-xl mt-2">
                <DateInput
                    label="Created End Date"
                    value={createdEndDate}
                    onBlur={value => setFilter("createdEndDate", value)}
                    disableAnimations
                    isClearable
                />
            </div>
            <div className="col-12 col-lg-6 col-xl-3 mt-2">
                <FormSelect
                    label="Advice Type"
                    defaultValue={adviceTypes}
                    onChange={selections => setFilter("adviceTypes", selections.map(selection => selection.value))}
                    options={adviceTypeOptions}
                    disableAnimations
                    isMulti
                />
            </div>
            <div className="col-12 col-lg-6 mt-2">
                <FormInput
                    label="Project Name"
                    value={projectName}
                    onChange={event => setFilter("projectName", event.target.value)}
                    disableAnimations
                />
            </div>
            <div className="col-12 col-lg-6 mt-2">
                <FormSelect
                    label="Project Status"
                    defaultValue={projectStatuses}
                    onChange={selections => setFilter("projectStatuses", selections.map(selection => selection.value))}
                    options={projectStatusOptions}
                    disableAnimations
                    isMulti
                />
            </div>
            <div className="col-12 col-lg-6 mt-2">
                <CampaignSelect
                    label="Campaign"
                    defaultValue={campaignId}
                    onChange={selection => setFilter("campaignId", selection.value)}
                    disableAnimations
                />
            </div>
            <div className="col-12 col-lg mt-2">
                <CampaignDependentStatusSelect
                    label="Campaign Status"
                    defaultValue={campaignStatusIds}
                    onChange={selections => setFilter("campaignStatusId", selections.map(selection => selection.value))}
                    disableAnimations
                    campaignId={campaignId}
                    isMulti
                />
            </div>
        </div>
    </>;
};

export default ProjectManagementFilters;
